import { useState, useEffect, useMemo } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import debounce from 'lodash/debounce'

type ImagePreviewFieldProps<T extends FieldValues> = {
  name: Path<T>
  control?: Control<T>
  label?: string
  className?: string
  containerClass?: string
  hintText?: string
  disabled?: boolean
  required?: boolean
  error?: string
}

const ImagePreviewField = <T extends FieldValues>({
  name,
  control,
  label = 'Image URL',
  className = '',
  containerClass = '',
  disabled = false,
  required = false,
  error: externalError,
}: ImagePreviewFieldProps<T>) => {
  const {
    field: { value, onChange, ref },
    fieldState: { error: internalError },
  } = useController({
    name,
    control,
  })

  const [inputValue, setInputValue] = useState(value || '')
  const [isImageLoading, setIsImageLoading] = useState(false)

  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  const debouncedUpdate = useMemo(
    () =>
      debounce((val: string) => {
        onChange(val)
        if (val) {
          setIsImageLoading(true)
        } else {
          control?.register(name)
        }
      }, 600),
    [onChange, name, control],
  )

  useEffect(() => {
    return () => debouncedUpdate.cancel()
  }, [debouncedUpdate])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    const val = e.target.value
    setInputValue(val)
    debouncedUpdate(val)
  }

  const handleImageLoad = () => {
    setIsImageLoading(false)
    control?.setError(name, { message: '' })
    control?._names.array.delete(name)
  }

  const handleImageError = () => {
    setIsImageLoading(false)
    if (value) {
      control?.setError(name, {
        type: 'manual',
        message: 'Valid URL format, but the image failed to load.',
      })
    }
  }

  const errorMessage = internalError?.message || externalError

  return (
    <div className={`relative w-full ${containerClass}`}>
      {label && (
        <label
          htmlFor={String(name)}
          className="mb-1 block text-xs font-semibold text-sidebar-primary"
        >
          {label} {required && <span className="text-error">*</span>}
        </label>
      )}

      <div
        className={`flex h-11 w-full items-center rounded-lg border px-3 ${
          errorMessage ? 'border-error' : 'border-gray-300'
        } ${disabled ? 'cursor-not-allowed bg-gray-200' : 'bg-white'}`}
      >
        <input
          id={String(name)}
          type="text"
          ref={ref}
          value={inputValue}
          onChange={handleInputChange}
          disabled={disabled}
          className={`h-full w-full bg-transparent text-sm text-primary outline-none ${className}`}
        />
      </div>

      {value && !errorMessage && (
        <div className="relative mt-2 h-44 w-full overflow-hidden rounded-md border bg-gray-100">
          {isImageLoading && (
            <div className="absolute inset-0 animate-pulse bg-gray-300" />
          )}
          <img
            key={value}
            src={value}
            onLoad={handleImageLoad}
            onError={handleImageError}
            alt="preview"
            className={`h-full w-auto object-contain p-1 transition-opacity ${
              isImageLoading ? 'opacity-0' : 'opacity-100'
            }`}
          />
        </div>
      )}

      {errorMessage && (
        <p className="mt-1 text-xs font-medium text-error">{errorMessage}</p>
      )}
    </div>
  )
}

export default ImagePreviewField
